import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import type { GetFeedArticlesPagedFromProjectionArticle } from "@/data-access/news-feeds/queries/articles-management-queries";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import type { SelectedFiltersInput } from "@/lib/utils/filters";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import type {
  ArticleEditState,
  FeedContentManagementSlice,
} from "./feed-content-management.slice";

export interface SelectionSnapshot {
  selectedIds: string[];
  selectedCount: number;
  isAllSelected: boolean;
}

/**
 * Builds a snapshot of the current article selection state.
 *
 * Determines which articles are selected based on three possible modes:
 * single article action, select-all with exclusions, or individual selection.
 * Returns the selected article IDs, count, and selection mode flag.
 *
 * This function handles three distinct selection scenarios:
 *
 * **Scenario 1: Single Article Selection**
 * - Triggered when `singleArticleIdForAction` is set (e.g., single delete/edit)
 * - Returns only the single article ID
 * - `selectedCount` is always 1
 * - `isAllSelected` is always false
 * - Used for actions targeting a specific article regardless of bulk selection state
 *
 * **Scenario 2: Select All with Exclusions**
 * - Triggered when `isAllSelected` is true
 * - All articles matching current filters are selected, except those in `excludedArticleIds`
 * - `selectedIds`: Array of all article IDs from `articlesMap` minus excluded IDs
 *   - Note: This only includes loaded articles. For true "select all", backend should use filters + excludedIds
 * - `selectedCount`: Calculated as `totalCount - excludedArticleIds.size`
 *   - Uses `pagination.totalCount` if available, otherwise falls back to `articlesMap.size`
 * - `isAllSelected` is true
 * - Used for bulk operations where user wants "all except these specific articles"
 *
 * **Scenario 3: Individual Selection**
 * - Triggered when `isAllSelected` is false and no single article action
 * - Only articles explicitly in `selectedArticleIds` are selected
 * - `selectedIds`: Array of IDs from `selectedArticleIds` Set
 * - `selectedCount`: Number of items in `selectedArticleIds`
 * - `isAllSelected` is false
 * - Used for manual multi-select where user picks specific articles
 *
 * @param state - The current feed content management state
 * @returns A snapshot containing selected IDs, count, and selection mode
 *
 * @example
 * Single article selection
 * const snapshot = buildSelectionSnapshot({ singleArticleIdForAction: "123", ... });
 *  Returns: { selectedIds: ["123"], selectedCount: 1, isAllSelected: false }
 *
 * @example
 * Select all with exclusions
 * const snapshot = buildSelectionSnapshot({
 *   isAllSelected: true,
 *   excludedArticleIds: new Set(["456", "789"]),
 *   pagination: { totalCount: 100 },
 *   ...
 * });
 * Returns: { selectedIds: [...all except 456, 789], selectedCount: 98, isAllSelected: true }
 *
 * @example
 * Individual selection
 * const snapshot = buildSelectionSnapshot({
 *   selectedArticleIds: new Set(["111", "222", "333"]),
 *   isAllSelected: false,
 *   ...
 * });
 * Returns: { selectedIds: ["111", "222", "333"], selectedCount: 3, isAllSelected: false }
 */
export const buildSelectionSnapshot = (
  state: FeedContentManagementSlice,
): SelectionSnapshot => {
  if (state.singleArticleIdForAction) {
    return {
      selectedIds: [state.singleArticleIdForAction],
      selectedCount: 1,
      isAllSelected: false,
    };
  }

  if (state.isAllSelected) {
    const selectedIds = Array.from(state.articlesMap.keys()).filter(
      (id) => !state.excludedArticleIds.has(id),
    );
    const totalCount = state.pagination.totalCount ?? state.articlesMap.size;

    return {
      selectedIds,
      selectedCount: Math.max(totalCount - state.excludedArticleIds.size, 0),
      isAllSelected: true,
    };
  }

  const selectedIds = Array.from(state.selectedArticleIds);

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    isAllSelected: false,
  };
};

/**
 * Compare two values for equality, handling null/undefined
 */
export const areValuesEqual = (
  value1: string | boolean | null | undefined,
  value2: string | boolean | null | undefined,
): boolean => {
  // Treat null and undefined as equal
  if (value1 == null && value2 == null) return true;
  return value1 === value2;
};

/** Order-independent equality for ID string arrays. */
export const areStringArraysEqual = (a: string[], b: string[]): boolean => {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && [...setA].every((id) => setB.has(id));
};

/**
 * Build overrides object by comparing current values with original values
 * For single article edit: only include changed fields, set null for unchanged
 * For bulk edit: include all fields
 */
export const buildOverridesObject = (
  editArticle: ArticleEditState,
  articlesMap: Map<string, GetFeedArticlesPagedFromProjectionArticle>,
  singleArticleId: string | null,
) => {
  // For bulk edit, return all fields as before
  if (!singleArticleId) {
    return {
      headline: editArticle.headline,
      summary: editArticle.summary,
      author: editArticle.author,
      category: editArticle.category,
      datePublished: editArticle.datePublished,
      publisher: editArticle.publisher,
      sentimentPolarity: editArticle.sentimentPolarity,
      sentimentRationale: editArticle.sentimentRationale,
      prominence: editArticle.prominence,
      isLede: editArticle.isLede,
      feedArticleTags: editArticle.articleTagIds.length
        ? editArticle.articleTagIds
        : null,
    };
  }

  // For single edit, get original article from map and compare
  const originalArticle = articlesMap.get(singleArticleId);
  if (!originalArticle) {
    // Fallback to sending all fields if we can't find the original
    return {
      headline: editArticle.headline,
      summary: editArticle.summary,
      author: editArticle.author,
      category: editArticle.category,
      datePublished: editArticle.datePublished,
      publisher: editArticle.publisher,
      sentimentPolarity: editArticle.sentimentPolarity,
      sentimentRationale: editArticle.sentimentRationale,
      prominence: editArticle.prominence,
      isLede: editArticle.isLede,
      feedArticleTags: editArticle.articleTagIds.length
        ? editArticle.articleTagIds
        : null,
    };
  }

  // Inline transformation from projection article to ArticleEditState format for comparison
  const originalAuthor = originalArticle.authors?.[0] ?? null;
  const originalCategory = originalArticle.contentCategory ?? null;
  const originalDatePublished = (() => {
    if (!originalArticle.datePublished) return null;
    try {
      const parsedDate = parseISO(originalArticle.datePublished);
      return formatInTimeZone(parsedDate, "UTC", "yyyy-MM-dd");
    } catch {
      return null;
    }
  })();
  const originalHeadline = originalArticle.headline ?? null;
  const originalSummary = originalArticle.summary ?? null;
  const originalPublisher = originalArticle.publisher ?? null;
  const originalSentimentPolarity = originalArticle.sentimentPolarity ?? null;
  const originalSentimentRationale = originalArticle.sentimentRationale ?? null;
  const originalProminence = originalArticle.prominence ?? null;
  const originalIsLede = originalArticle.isLede ?? null;
  const originalArticleTagIds = originalArticle.articleTags.map(
    (tag) => tag.id,
  );

  // Compare and only send changed values
  return {
    headline: areValuesEqual(editArticle.headline, originalHeadline)
      ? null
      : editArticle.headline,
    summary: areValuesEqual(editArticle.summary, originalSummary)
      ? null
      : editArticle.summary,
    author: areValuesEqual(editArticle.author, originalAuthor)
      ? null
      : editArticle.author,
    category: areValuesEqual(editArticle.category, originalCategory)
      ? null
      : editArticle.category,
    datePublished: areValuesEqual(
      editArticle.datePublished,
      originalDatePublished,
    )
      ? null
      : editArticle.datePublished,
    publisher: areValuesEqual(editArticle.publisher, originalPublisher)
      ? null
      : editArticle.publisher,
    sentimentPolarity: areValuesEqual(
      editArticle.sentimentPolarity,
      originalSentimentPolarity,
    )
      ? null
      : editArticle.sentimentPolarity,
    sentimentRationale: areValuesEqual(
      editArticle.sentimentRationale,
      originalSentimentRationale,
    )
      ? null
      : editArticle.sentimentRationale,
    prominence: areValuesEqual(editArticle.prominence, originalProminence)
      ? null
      : editArticle.prominence,
    isLede: areValuesEqual(editArticle.isLede, originalIsLede)
      ? null
      : editArticle.isLede,
    feedArticleTags: areStringArraysEqual(
      editArticle.articleTagIds,
      originalArticleTagIds,
    )
      ? null
      : editArticle.articleTagIds,
  };
};

/**
 * Builds the selection input structure for bulk operations (delete, update, move)
 * Handles three cases:
 * 1. Select all with excluded IDs → returns projectionFilters + excludedIds array
 * 2. Select all with no excluded IDs → returns projectionFilters + excludedIds: null
 * 3. Individual selection → returns feedArticleIds array + projectionFilters: null
 *
 * @param state - The feed content management state
 * @param dateRange - Date range for filters
 * @param selectedFilters - Selected filters to include
 * @returns Object with excludedIds, feedArticleIds, and projectionFilters
 */
export const buildBulkOperationInput = (
  state: FeedContentManagementSlice,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: SelectedFiltersInput,
): {
  excludedIds: string[] | null;
  feedArticleIds: string[] | null;
  projectionFilters:
    | ({
        dateRange: { startDate: string; endDate: string };
        searchMatch?: string;
      } & SelectedFiltersInput)
    | null;
} => {
  const { selectedIds, isAllSelected } = buildSelectionSnapshot(state);
  const { excludedArticleIds, searchTerm } = state;

  // Combine dateRange, searchTerm, and selectedFilters into projection filters object
  const projectionFilters = {
    dateRange: { startDate: dateRange.startDate, endDate: dateRange.endDate },
    ...(searchTerm && { searchMatch: searchTerm }),
    ...selectedFilters,
  };

  if (isAllSelected) {
    // Case 1: Select all with some excluded IDs
    if (excludedArticleIds.size > 0) {
      return {
        excludedIds: Array.from(excludedArticleIds),
        feedArticleIds: null,
        projectionFilters,
      };
    }
    // Case 2: Select all with no excluded IDs
    return {
      excludedIds: null,
      feedArticleIds: null,
      projectionFilters,
    };
  }

  // Case 3: Individual selection - use feedArticleIds
  return {
    excludedIds: null,
    feedArticleIds: selectedIds,
    projectionFilters: null,
  };
};

/**
 * Drops selected narrative clusters that are missing from the refetched
 * NARRATIVE_CLUSTER sidebar items, which only list clusters that still have
 * articles. Returns null when nothing changes or the items never loaded.
 */
export const removeEmptiedClustersFromSelected = (
  selected: Partial<Record<ShareVoiceSidebarCategory, string[]>>,
): Partial<Record<ShareVoiceSidebarCategory, string[]>> | null => {
  const selectedIds = selected.NARRATIVE_CLUSTER;
  const clustersWithArticles = useNewsFeedFilters
    .getState()
    .dynamicFilters.get("NARRATIVE_CLUSTER")?.itemsMap;
  if (!selectedIds || !clustersWithArticles) return null;

  const remaining = selectedIds.filter((id) => clustersWithArticles.has(id));
  if (remaining.length === selectedIds.length) return null;

  const { NARRATIVE_CLUSTER: _, ...otherFilters } = selected;
  return remaining.length > 0
    ? { ...otherFilters, NARRATIVE_CLUSTER: remaining }
    : otherFilters;
};
